<template>
  <div class="bg-background-light text-slate-800 antialiased min-h-screen flex flex-col md:flex-row md:h-screen md:overflow-hidden">
    <ClientSidebar />

    <main class="flex-1 flex flex-col min-h-0 overflow-hidden">

      <!-- Header -->
      <header class="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Documents</h2>
          <p class="text-xs text-slate-500">Manage and view your tax documents and files.</p>
        </div>
        <button
          v-if="filterCategory !== 'Correspondence'"
          @click="openUploadModal"
          class="flex items-center gap-2 px-3 md:px-4 py-2 bg-primary text-[#282F44] text-sm font-bold rounded-lg hover:bg-primary-hover transition-colors">
          <span class="material-symbols-outlined !text-[18px]">upload_file</span>
          <span class="hidden sm:inline">Upload New</span>
        </button>
      </header>

      <div class="flex-1 overflow-y-auto p-4 md:p-8">

        <!-- Loading -->
        <div v-if="loading" class="flex flex-col items-center justify-center h-full gap-4">
          <svg class="animate-spin h-10 w-10 text-brand-dark" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-slate-400 text-sm">Loading your documents...</p>
        </div>

        <div v-else class="max-w-[1200px] mx-auto space-y-8">

          <!-- ── Folders ── -->
          <section>
            <div class="flex items-center gap-2 mb-4">
              <span class="material-symbols-outlined text-primary !text-[20px]">folder_copy</span>
              <h3 class="text-base font-bold text-slate-900">Folders</h3>
            </div>
            <div class="grid grid-cols-2 gap-4">

              <!-- Uploaded Documents -->
              <div
                @click="setFolderFilter('Uploaded Documents')"
                class="bg-white border rounded-xl p-5 flex flex-col gap-3 cursor-pointer transition-all hover:shadow-md hover:border-primary/30 group"
                :class="filterCategory === 'Uploaded Documents' ? 'border-primary/50 shadow-sm' : 'border-slate-200'">
                <div class="flex items-start justify-between">
                  <div class="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <span class="material-symbols-outlined text-amber-500 !text-[22px]">folder_shared</span>
                  </div>
                  <span class="text-xs text-slate-400 font-medium">
                    {{ folderCounts['Uploaded Documents'] }} files
                  </span>
                </div>
                <div>
                  <p class="text-sm font-bold text-slate-800 group-hover:text-primary transition-colors">Uploaded Docs</p>
                  <p class="text-xs text-slate-400 mt-0.5">{{ folderSizes['Uploaded Documents'] }}</p>
                </div>
              </div>

              <!-- Correspondence -->
              <div
                @click="setFolderFilter('Correspondence')"
                class="bg-white border rounded-xl p-5 flex flex-col gap-3 cursor-pointer transition-all hover:shadow-md hover:border-primary/30 group"
                :class="filterCategory === 'Correspondence' ? 'border-primary/50 shadow-sm' : 'border-slate-200'">
                <div class="flex items-start justify-between">
                  <div class="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <span class="material-symbols-outlined text-green-600 !text-[22px]">receipt_long</span>
                  </div>
                  <span class="text-xs text-slate-400 font-medium">
                    {{ folderCounts['Correspondence'] }} files
                  </span>
                </div>
                <div>
                  <p class="text-sm font-bold text-slate-800 group-hover:text-primary transition-colors">Correspondence</p>
                  <p class="text-xs text-slate-400 mt-0.5">{{ folderSizes['Correspondence'] }}</p>
                </div>
              </div>


            </div>
          </section>

          <!-- ── All Files Table ── -->
          <section>
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary !text-[20px]">description</span>
                <h3 class="text-base font-bold text-slate-900">
                  {{ filterCategory ? filterCategory : 'All Documents' }}
                </h3>
                <span class="text-xs text-slate-400 font-medium ml-1">
                  {{ filteredFiles.length }} files
                </span>
              </div>
              <!-- Active filter tag -->
              <button
                v-if="filterCategory"
                @click="filterCategory = ''"
                class="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                {{ filterCategory }}
                <span class="material-symbols-outlined !text-[14px]">close</span>
              </button>
            </div>

            <!-- Empty state -->
            <div v-if="filteredFiles.length === 0" class="bg-white rounded-xl border border-slate-200 p-16 flex flex-col items-center text-center">
              <span class="material-symbols-outlined text-slate-300 !text-[48px] mb-3">folder_open</span>
              <p class="text-slate-700 font-semibold mb-1">No files found</p>
              <p class="text-slate-400 text-sm mb-4">
                No files in this folder yet.
              </p>
              <button
                v-if="filterCategory !== 'Correspondence'"
          @click="openUploadModal"
                class="flex items-center gap-2 px-4 py-2 bg-[#282F44] text-white text-sm font-bold rounded-lg hover:bg-[#1a1f2e] transition-colors">
                <span class="material-symbols-outlined !text-[16px]">upload_file</span>
                Upload a File
              </button>
            </div>

            <!-- Table -->
            <div v-else class="bg-white rounded-xl border border-slate-200 overflow-x-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="border-b border-slate-100 bg-slate-50">
                    <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                    <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date Modified</th>
                    <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th class="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Size</th>
                    <th class="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr
                    v-for="file in paginatedFiles"
                    :key="file.FileUrl"
                    class="hover:bg-slate-50 transition-colors group">

                    <!-- Name -->
                    <td class="px-5 py-3.5">
                      <div class="flex items-center gap-3">
                        <div
                          class="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          :class="getFileBg(file.Name)">
                          <span
                            class="material-symbols-outlined !text-[16px]"
                            :class="getFileIconColor(file.Name)">
                            {{ getFileIcon(file.Name) }}
                          </span>
                        </div>
                        <div>
                          <p class="font-medium text-slate-800 truncate max-w-[280px]">{{ file.Name }}</p>
                          <p class="text-xs text-slate-400">
                            {{ file.Category === 'Correspondence' ? 'Added by Admin' : 'Uploaded by You' }}
                          </p>
                        </div>
                      </div>
                    </td>

                    <!-- Date -->
                    <td class="px-5 py-3.5 text-slate-500 text-sm">
                      {{ formatDate(file.Modified || file.Created) }}
                    </td>

                    <!-- Type badge -->
                    <td class="px-5 py-3.5">
                      <span
                        class="px-2 py-0.5 rounded-md text-xs font-semibold"
                        :class="getTypeBadgeClass(file.Name)">
                        {{ getTypeLabel(file.Name) }}
                      </span>
                    </td>

                    <!-- Size -->
                    <td class="px-5 py-3.5 text-slate-500 text-sm">
                      {{ formatFileSize(file.Size) }}
                    </td>

                    <!-- Actions -->
                    <td class="px-5 py-3.5">
                      <div class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a
                          :href="buildFileUrl(file)"
                          target="_blank"
                          class="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Download">
                          <span class="material-symbols-outlined !text-[16px]">download</span>
                        </a>

                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div class="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50">
                <p class="text-xs text-slate-400">
                  Showing {{ paginationStart }}-{{ paginationEnd }} of {{ filteredFiles.length }} files
                </p>
                <div class="flex items-center gap-1">
                  <button
                    @click="currentPage--"
                    :disabled="currentPage === 1"
                    class="h-7 w-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <span class="material-symbols-outlined !text-[16px]">chevron_left</span>
                  </button>
                  <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="currentPage = page"
                    class="h-7 w-7 rounded-lg text-xs font-semibold transition-colors"
                    :class="currentPage === page
                      ? 'bg-[#282F44] text-white'
                      : 'text-slate-500 hover:bg-slate-200'">
                    {{ page }}
                  </button>
                  <button
                    @click="currentPage++"
                    :disabled="currentPage === totalPages"
                    class="h-7 w-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    <span class="material-symbols-outlined !text-[16px]">chevron_right</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- Disclaimer -->
      <p class="px-4 md:px-8 lg:px-12 py-4 text-center leading-relaxed" style="font-size:9px; font-style:italic; color:rgba(0,0,0,0.8);">
        <span class="font-semibold not-italic">Disclaimer:</span> By uploading documents to this portal, you acknowledge that while we employ industry-standard security to protect your data, the transmission of information over the internet is not entirely risk-free. ABC TAX ADVISORY utilizes this third-party platform for your convenience but assumes no responsibility for data security or technical issues arising from its use.
      </p>
    </main>

    <!-- ── Success Toast ── -->
    <transition name="fade">
      <div v-if="uploadSuccess" class="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#282F44] text-white px-5 py-3 rounded-xl shadow-xl flex items-center gap-2 text-sm font-semibold pointer-events-none">
        <span class="material-symbols-outlined !text-[18px] text-green-400">check_circle</span>
        {{ uploadSuccess }}
      </div>
    </transition>

    <!-- ── Upload Modal ── -->
    <div v-if="showUploadModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="closeUploadModal"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden z-10">

        <div class="h-1.5 w-full bg-[#282F44]"></div>

        <div class="p-6">
          <div class="flex items-center justify-between mb-5">
            <h3 class="text-base font-bold text-slate-900">Upload Document</h3>
            <button @click="closeUploadModal" class="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-colors">
              <span class="material-symbols-outlined !text-[18px]">close</span>
            </button>
          </div>

          <!-- ── Type toggle ── -->
          <div class="flex gap-2 mb-5 p-1 bg-slate-100 rounded-xl">
            <button
              type="button"
              @click="uploadType = 'document'; uploadFile = null; uploadError = ''"
              class="flex-1 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5"
              :class="uploadType === 'document' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
              <span class="material-symbols-outlined !text-[16px]">description</span>
              Document
            </button>
            <button
              type="button"
              @click="uploadType = 'receipt'; uploadFile = null; uploadError = ''"
              class="flex-1 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5"
              :class="uploadType === 'receipt' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
              <span class="material-symbols-outlined !text-[16px]">receipt_long</span>
              Receipt
            </button>
          </div>

          <!-- ── Receipt name ── -->
          <div v-if="uploadType === 'receipt'" class="mb-4">
            <label class="block text-xs font-semibold text-slate-600 mb-1.5">Receipt Name <span class="text-red-400">*</span></label>
            <input
              v-model="receiptName"
              type="text"
              placeholder="e.g. Office Supplies Jan 2025"
              class="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
          </div>

          <!-- ── Document drop zone ── -->
          <div v-if="uploadType === 'document'"
            class="border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer mb-4"
            :class="isDragging ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/50'"
            @dragover.prevent="isDragging = true"
            @dragleave="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.uploadInput.click()">
            <span class="material-symbols-outlined text-slate-300 !text-[40px] mb-2">cloud_upload</span>
            <p class="text-sm font-semibold text-slate-700">Drop your file here or click to browse</p>
            <p class="text-xs text-slate-400 mt-1">PDF, Word, Excel, Images — Max 25MB</p>
            <input ref="uploadInput" type="file" class="hidden" @change="onUploadFileSelected" accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png" />
          </div>

          <!-- ── Receipt upload area ── -->
          <div v-if="uploadType === 'receipt'" class="mb-4">
            <!-- Mobile: camera + file buttons -->
            <div v-if="isMobile" class="grid grid-cols-2 gap-3">
              <button type="button" @click="$refs.cameraInput.click()"
                class="border-2 border-dashed border-slate-200 hover:border-primary/50 rounded-xl p-5 flex flex-col items-center gap-2 transition-all">
                <span class="material-symbols-outlined text-slate-400 !text-[32px]">photo_camera</span>
                <span class="text-xs font-semibold text-slate-600">Take Photo</span>
              </button>
              <button type="button" @click="$refs.receiptInput.click()"
                class="border-2 border-dashed border-slate-200 hover:border-primary/50 rounded-xl p-5 flex flex-col items-center gap-2 transition-all">
                <span class="material-symbols-outlined text-slate-400 !text-[32px]">upload_file</span>
                <span class="text-xs font-semibold text-slate-600">Upload File</span>
              </button>
              <input ref="cameraInput" type="file" accept="image/*" capture="environment" class="hidden" @change="onUploadFileSelected" />
              <input ref="receiptInput" type="file" accept=".pdf,.jpg,.jpeg,.png" class="hidden" @change="onUploadFileSelected" />
            </div>
            <!-- Desktop: drop zone -->
            <div v-else
              class="border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer"
              :class="isDragging ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/50'"
              @dragover.prevent="isDragging = true"
              @dragleave="isDragging = false"
              @drop.prevent="handleDrop"
              @click="$refs.receiptDesktopInput.click()">
              <span class="material-symbols-outlined text-slate-300 !text-[40px] mb-2">receipt</span>
              <p class="text-sm font-semibold text-slate-700">Drop receipt here or click to browse</p>
              <p class="text-xs text-slate-400 mt-1">PDF, JPG, PNG — Max 25MB</p>
              <input ref="receiptDesktopInput" type="file" accept=".pdf,.jpg,.jpeg,.png" class="hidden" @change="onUploadFileSelected" />
            </div>
          </div>

          <!-- Selected file preview -->
          <div v-if="uploadFile" class="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl mb-4">
            <div class="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" :class="getFileBg(uploadFile.name)">
              <span class="material-symbols-outlined !text-[18px]" :class="getFileIconColor(uploadFile.name)">{{ getFileIcon(uploadFile.name) }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-slate-800 truncate">{{ uploadFile.name }}</p>
              <p class="text-xs text-slate-400">{{ formatFileSize(uploadFile.size) }}</p>
            </div>
            <button @click="uploadFile = null" class="text-slate-400 hover:text-red-500 transition-colors">
              <span class="material-symbols-outlined !text-[18px]">close</span>
            </button>
          </div>

          <!-- Error -->
          <div v-if="uploadError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center gap-2">
            <span class="material-symbols-outlined !text-[16px]">error</span>
            {{ uploadError }}
          </div>

          <!-- Actions -->
          <div class="flex gap-3">
            <button
              @click="closeUploadModal"
              class="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button
              @click="handleUpload"
              :disabled="!uploadFile || uploading || (uploadType === 'receipt' && !receiptName.trim())"
              class="flex-1 py-2.5 bg-[#282F44] hover:bg-[#1a1f2e] text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg v-if="uploading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="material-symbols-outlined !text-[16px]" v-else>{{ uploadType === 'receipt' ? 'receipt_long' : 'upload' }}</span>
              {{ uploading ? 'Uploading...' : (uploadType === 'receipt' ? 'Save Receipt' : 'Upload File') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Delete Confirm Modal ── -->
    <div v-if="fileToDelete" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="fileToDelete = null"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-[400px] p-6 z-10">
        <div class="flex flex-col items-center text-center gap-3 mb-5">
          <div class="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
            <span class="material-symbols-outlined text-red-500 !text-[24px]">delete</span>
          </div>
          <h3 class="text-base font-bold text-slate-900">Delete File?</h3>
          <p class="text-sm text-slate-500">
            Are you sure you want to delete <strong>{{ fileToDelete?.Name }}</strong>? This cannot be undone.
          </p>
        </div>
        <div class="flex gap-3">
          <button
            @click="fileToDelete = null"
            class="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button
            @click="handleDelete"
            :disabled="deleting"
            class="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            <svg v-if="deleting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ClientSidebar from '../../components/ClientSidebar.vue';

export default {
  name: 'ClientDocuments',
  components: { ClientSidebar },

  data() {
    return {
      loading: true,
      allFiles: [],
      filterCategory: '',
      currentPage: 1,
      perPage: 10,

      // Upload modal
      showUploadModal: false,
      uploadFile: null,
      uploadCategory: 'Uploaded Documents',
      uploading: false,
      uploadError: '',
      isDragging: false,
      // Receipt
      uploadType: 'document',
      receiptName: '',
      isMobile: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent),
      uploadSuccess: '',

      // Delete
      fileToDelete: null,
      deleting: false
    };
  },

  computed: {
    clientRecord() {
      try {
        return JSON.parse(sessionStorage.getItem('clientRecord') || '{}');
      } catch { return {}; }
    },

    clientName() {
      return this.clientRecord?.name || '';
    },

    // ── Folder counts + sizes ──────────────────────────────────────
    folderCounts() {
      return {
        'Uploaded Documents': this.allFiles.filter(f => f.Category === 'Uploaded Documents').length,
        'Correspondence': this.allFiles.filter(f => f.Category === 'Correspondence').length
      };
    },

    folderSizes() {
      const calc = (cat) => {
        const total = this.allFiles
          .filter(f => f.Category === cat)
          .reduce((sum, f) => sum + (f.Size || 0), 0);
        return this.formatFileSize(total);
      };
      return {
        'Uploaded Documents': calc('Uploaded Documents'),
        'Correspondence': calc('Correspondence')
      };
    },

    totalSize() {
      const total = this.allFiles.reduce((sum, f) => sum + (f.Size || 0), 0);
      return this.formatFileSize(total);
    },

    // ── Filtered files ─────────────────────────────────────────────
    filteredFiles() {
      let files = [...this.allFiles];

      if (this.filterCategory) {
        files = files.filter(f => f.Category === this.filterCategory);
      }

      return files.sort((a, b) => new Date(b.Modified || b.Created) - new Date(a.Modified || a.Created));
    },

    // ── Pagination ─────────────────────────────────────────────────
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredFiles.length / this.perPage));
    },

    paginatedFiles() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredFiles.slice(start, start + this.perPage);
    },

    paginationStart() {
      if (this.filteredFiles.length === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.filteredFiles.length);
    }
  },

  watch: {
    filterCategory() { this.currentPage = 1; }
  },

  async mounted() {
    await this.loadFiles();

    // If routed with a folder pre-selected (e.g. from dashboard "View All")
    if (this.$route.query.folder) {
      this.filterCategory = this.$route.query.folder;
    }
  },

  methods: {
    async loadFiles() {
      this.loading = true;
      try {
        this.allFiles = await this.$sharepoint.getClientFiles(this.clientName);
      } catch (error) {
        console.error('Error loading files:', error);
      } finally {
        this.loading = false;
      }
    },

    setFolderFilter(category) {
      this.filterCategory = this.filterCategory === category ? '' : category;
    },

    // ── Upload ─────────────────────────────────────────────────────
    openUploadModal() {
      this.showUploadModal = true;
      this.uploadFile = null;
      this.uploadError = '';
      this.uploadCategory = 'Uploaded Documents';
      this.uploadType = 'document';
      this.receiptName = '';
    },

    closeUploadModal() {
      if (this.uploading) return;
      this.showUploadModal = false;
      this.uploadFile = null;
      this.uploadError = '';
      this.uploadType = 'document';
      this.receiptName = '';
    },

    onUploadFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 25 * 1024 * 1024) {
          this.uploadError = 'File exceeds the 25MB limit.';
          return;
        }
        this.uploadFile = file;
        this.uploadError = '';
      }
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        if (file.size > 25 * 1024 * 1024) {
          this.uploadError = 'File exceeds the 25MB limit.';
          return;
        }
        this.uploadFile = file;
        this.uploadError = '';
      }
    },

    async handleUpload() {
      if (!this.uploadFile || this.uploading) return;
      if (this.uploadType === 'receipt' && !this.receiptName.trim()) {
        this.uploadError = 'Please enter a receipt name.';
        return;
      }
      this.uploading = true;
      this.uploadError = '';
      try {
        const category = this.uploadType === 'receipt'
          ? `Uploaded Documents/${this.receiptName.trim()}`
          : this.uploadCategory;
        await this.$sharepoint.uploadDocument(this.uploadFile, {
          clientName: this.clientName,
          category
        });
        // Reload so receipt subfolders appear correctly
        this.allFiles = await this.$sharepoint.getClientFiles(this.clientName);
        const successMsg = this.uploadType === 'receipt'
          ? `Receipt "${this.receiptName.trim()}" uploaded successfully!`
          : 'File uploaded successfully!';
        this.closeUploadModal();
        this.uploadSuccess = successMsg;
        setTimeout(() => { this.uploadSuccess = ''; }, 4000);
      } catch (error) {
        console.error('Upload error:', error);
        this.uploadError = 'Upload failed. Please try again.';
      } finally {
        this.uploading = false;
      }
    },

    // ── Delete ─────────────────────────────────────────────────────
    confirmDelete(file) {
      this.fileToDelete = file;
    },

    async handleDelete() {
      if (!this.fileToDelete || this.deleting) return;
      this.deleting = true;
      try {
        await this.$sharepoint.deleteDocument(this.fileToDelete.FileUrl);
        this.allFiles = this.allFiles.filter(f => f.FileUrl !== this.fileToDelete.FileUrl);
        this.fileToDelete = null;
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete file. Please try again.');
      } finally {
        this.deleting = false;
      }
    },

    // ── Helpers ────────────────────────────────────────────────────
    getFileType(filename) {
      const ext = (filename || '').split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(ext)) return 'word';
      if (['xlsx', 'xls'].includes(ext)) return 'excel';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
      return 'file';
    },

    getFileIcon(filename) {
      const map = { pdf: 'picture_as_pdf', word: 'description', excel: 'table_view', image: 'image', file: 'insert_drive_file' };
      return map[this.getFileType(filename)] || 'insert_drive_file';
    },

    getFileBg(filename) {
      const map = { pdf: 'bg-red-50', word: 'bg-blue-50', excel: 'bg-green-50', image: 'bg-purple-50', file: 'bg-slate-50' };
      return map[this.getFileType(filename)] || 'bg-slate-50';
    },

    getFileIconColor(filename) {
      const map = { pdf: 'text-red-500', word: 'text-blue-500', excel: 'text-green-500', image: 'text-purple-500', file: 'text-slate-400' };
      return map[this.getFileType(filename)] || 'text-slate-400';
    },

    getTypeLabel(filename) {
      const map = { pdf: 'PDF', word: 'Word', excel: 'Excel', image: 'Image', file: 'File' };
      return map[this.getFileType(filename)] || 'File';
    },

    getTypeBadgeClass(filename) {
      const map = {
        pdf: 'bg-red-50 text-red-600',
        word: 'bg-blue-50 text-blue-600',
        excel: 'bg-green-50 text-green-600',
        image: 'bg-purple-50 text-purple-600',
        file: 'bg-slate-100 text-slate-600'
      };
      return map[this.getFileType(filename)] || 'bg-slate-100 text-slate-600';
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    },

    buildFileUrl(file) {
      const token = sessionStorage.getItem('clientSession') || '';
      if (file.DriveItemId && file.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(file.DriveId)}&itemId=${encodeURIComponent(file.DriveItemId)}&token=${encodeURIComponent(token)}`;
      }
      return file.FileUrl || '#';
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 KB';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>